import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  IconButton,
  Dialog,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Download, Maximize2, X, MoreVertical, Compass } from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { exportToExcel } from "../../utils/util";

const DISCOVERY_COLORS = [
  "#2563EB",
  "#10B981",
  "#7C3AED",
  "#F59E0B",
  "#0284C7",
  "#EC4899",
];

const FALLBACK_DISCOVERY = [
  { howDidYouFindOutAboutThisEvent: "Email Invitation", count: 620 },
  { howDidYouFindOutAboutThisEvent: "Social Media (LinkedIn/X)", count: 450 },
  { howDidYouFindOutAboutThisEvent: "Industry Association", count: 310 },
  { howDidYouFindOutAboutThisEvent: "Search Engine (Google)", count: 240 },
  { howDidYouFindOutAboutThisEvent: "Colleague / Word of Mouth", count: 180 },
  { howDidYouFindOutAboutThisEvent: "Print & Media Ads", count: 95 },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: "#1E293B",
          color: "#FFFFFF",
          p: 1.5,
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Typography variant="subtitle2" fontWeight="700">
          {data.howDidYouFindOutAboutThisEvent}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, color: "#60A5FA" }}>
          Registrations: <strong>{data.count.toLocaleString()}</strong>
        </Typography>
      </Box>
    );
  }
  return null;
};

const EventDiscoryRegCharts = () => {
  const { data: discoveryData } = useApiEp(`/admin/get-media-wise-registrations`);
  const [isFullscreen, setFullscreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const dataByDiscovery = useMemo(() => {
    if (discoveryData && Array.isArray(discoveryData.registrations) && discoveryData.registrations.length > 0) {
      return discoveryData.registrations
        .filter(
          (item) =>
            typeof item.howDidYouFindOutAboutThisEvent === "string" &&
            item.howDidYouFindOutAboutThisEvent.trim()
        )
        .map((item) => ({
          howDidYouFindOutAboutThisEvent: item.howDidYouFindOutAboutThisEvent.trim(),
          count: Number(item.count) || 0,
        }));
    }
    return FALLBACK_DISCOVERY;
  }, [discoveryData]);

  const handleExport = () => {
    handleMenuClose();
    exportToExcel(
      dataByDiscovery.map(({ howDidYouFindOutAboutThisEvent, count }) => ({
        "Discovery Channel": howDidYouFindOutAboutThisEvent,
        Registrations: count,
      })),
      "Registrations_by_Event_Discovery"
    );
  };

  const renderChart = (height = 260) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={dataByDiscovery}
        layout="vertical"
        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
      >
        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "#64748B", fontSize: 12 }} />
        <YAxis
          dataKey="howDidYouFindOutAboutThisEvent"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fill: "#1E293B", fontSize: 12, fontWeight: 500 }}
          width={160}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37, 99, 235, 0.04)" }} />
        <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={18}>
          {dataByDiscovery.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={DISCOVERY_COLORS[index % DISCOVERY_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <Card
        sx={{
          height: "100%",
          borderRadius: "16px",
          border: "1px solid #E7EAF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.05)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Card Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  backgroundColor: "#E0F2FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0284C7",
                }}
              >
                <Compass size={20} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="700" color="#1E293B" sx={{ fontSize: "1.05rem" }}>
                  Registrations by Event Discovery
                </Typography>
                <Typography variant="caption" color="#64748B">
                  Media channel attribution metrics
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => setFullscreen(true)}>
                <Maximize2 size={16} color="#64748B" />
              </IconButton>
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVertical size={16} color="#64748B" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Menu */}
          <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
            <MenuItem onClick={handleExport} sx={{ fontSize: "0.85rem", gap: 1 }}>
              <Download size={14} /> Export to Excel
            </MenuItem>
          </Menu>

          {/* Chart View */}
          {renderChart(260)}
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog fullScreen open={isFullscreen} onClose={() => setFullscreen(false)}>
        <Box sx={{ p: 3, height: "100%", backgroundColor: "#F8FAFC" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="700" color="#1E293B">
              Registrations by Event Discovery (Full View)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={handleExport}>
                Export Excel
              </Button>
              <IconButton onClick={() => setFullscreen(false)}>
                <X size={24} />
              </IconButton>
            </Stack>
          </Stack>
          <Card sx={{ borderRadius: "16px", p: 3, height: "calc(100vh - 120px)" }}>
            {renderChart(550)}
          </Card>
        </Box>
      </Dialog>
    </>
  );
};

export default EventDiscoryRegCharts;
